import React from "react";
import Reveal from "./Reveal";
import { DollarSign, Heart, Sparkles, Users, Mountain } from "lucide-react";

const pillars = [
  {
    icon: DollarSign,
    name: "Financial Stability",
    tagline: "Wealth with intention",
    description:
      "A living roadmap of goals and tasks — investments, business, creative financing. Real-time coaching on decisions with nudges that preserve your agency.",
    tint: "#3e6b52",
    bg: "#e9f0ec",
  },
  {
    icon: Heart,
    name: "Health",
    tagline: "Daily habits that fit you",
    description:
      "We research your profile, test diet frameworks (carnivore, Mediterranean, and more) and design the protocol you’ll actually follow — religiously.",
    tint: "#b94e4e",
    bg: "#f5e6e6",
  },
  {
    icon: Sparkles,
    name: "Spirituality",
    tagline: "Clarity. Presence. Service.",
    description:
      "Morning meditation, evening reflection, prayer, and helping others — tracked as a set of daily habits your coach reinforces, not lectures.",
    tint: "#7a5fb0",
    bg: "#ede8f5",
  },
  {
    icon: Users,
    name: "Relationships",
    tagline: "Love on purpose",
    description:
      "Find the right person. Daily reminders for personal growth in care and love. Specific ways to improve their life as you improve yours.",
    tint: "#c18848",
    bg: "#f5ece0",
  },
  {
    icon: Mountain,
    name: "Endgame",
    tagline: "Your ultimate picture",
    description:
      "The big picture anchors everything. Every goal ladders up. Your coach keeps you aware, keeps you on the path, keeps you true.",
    tint: "#486a8f",
    bg: "#e7edf5",
  },
];

export default function Pillars() {
  return (
    <section id="pillars" className="relative py-24 md:py-32 bg-[#faf6ec]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl mb-16">
            <p className="font-mono-eyebrow text-[#486a8f] mb-4">The 5 pillar framework</p>
            <h2 className="font-display text-[40px] md:text-[56px] leading-[1.05] text-[#0f1724] tracking-tight">
              One system.{" "}
              <span className="italic font-light text-[#486a8f]">Five pillars.</span>
            </h2>
            <p className="font-body text-[17px] md:text-[19px] text-[#4b5563] mt-5 leading-relaxed">
              Your whole life is connected. Ascend treats it that way — financial,
              health, spirituality, relationships, and your endgame — one coherent daily system.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.name} delay={i * 0.08}>
                <div className="group relative h-full rounded-2xl bg-white border border-[#eadfc9] p-8 card-hover overflow-hidden">
                  <div
                    className="absolute -right-16 -top-16 w-48 h-48 rounded-full opacity-40 blur-2xl transition-opacity group-hover:opacity-70"
                    style={{ background: p.bg }}
                  />
                  <div
                    className="relative w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                    style={{ background: p.bg }}
                  >
                    <Icon size={22} style={{ color: p.tint }} />
                  </div>
                  <p
                    className="relative font-mono-eyebrow mb-2"
                    style={{ color: p.tint }}
                  >
                    {p.tagline}
                  </p>
                  <h3 className="relative font-display text-[26px] text-[#0f1724] mb-3 tracking-tight leading-tight">
                    {p.name}
                  </h3>
                  <p className="relative font-body text-[15px] leading-relaxed text-[#4b5563]">
                    {p.description}
                  </p>
                </div>
              </Reveal>
            );
          })}

          {/* Feature callout card */}
          <Reveal delay={pillars.length * 0.08}>
            <div className="h-full rounded-2xl bg-gradient-to-br from-[#0f1724] to-[#1c2944] border border-[#0f1724] text-white p-8 flex flex-col justify-between">
              <div>
                <p className="font-mono-eyebrow text-[#8aa7c4] mb-4">Customizable coach</p>
                <h3 className="font-display text-[26px] tracking-tight leading-tight mb-3">
                  A coach that fits{" "}
                  <span className="italic font-light text-[#e9d8a6]">you</span>.
                </h3>
                <p className="font-body text-[15px] leading-relaxed text-white/80">
                  Hard & straight. Calm and collected. No-BS mentor. Best friend.
                  Pick male or female, pick an accent — your coach learns your
                  personality and meets you where you are.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Hard", "Calm", "No-BS", "Friend"].map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[12px] font-medium text-white/90"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
